import {
  type LoaderFunctionArgs,
  type ClientLoaderFunctionArgs,
  type MetaFunction,
  useLoaderData,
  redirectDocument,
} from 'react-router';

import QuizPage from '../../src/pages/QuizPage';
import QuizUnavailablePage from '../../src/pages/QuizUnavailablePage';
import { createSupabaseClientForLoader } from '../../src/lib/supabase-loader';
import { supabase } from '../../src/lib/supabase';
import type { QuizRouteData } from '../../src/types/routes';
import { loadQuizRoute } from '../../src/lib/services/quizRoute';
import { getCanonicalUrl } from '../../src/lib/utils/url';
import { routes, xpTrackerRouteHandle } from '../../src/routes';
import { getNotFoundPageMetadata } from '../../src/constants/pageMetadata';
import { profileSsgLoader } from '../../scripts/lib/ssg-build-profile.mjs';
import { isEmbedEditorPreviewSearch } from '../../src/lib/embed/embedEditorPreview';
import { loadQuizGameplayModule } from '../../src/pages/quizGameplayModule';

export const handle = xpTrackerRouteHandle;

async function loadDemoTheme(slug: string, url: URL) {
  const themeId = url.searchParams.get('demo_theme');
  if (
    slug !== 'newsletter-engagement-demo' ||
    url.searchParams.get('view') !== 'iframe' ||
    isEmbedEditorPreviewSearch(url.search) ||
    !themeId
  )
    return null;
  const { getNewsletterDemoTheme } = await import('../../src/lib/quiz/newsletterDemoThemes');
  return getNewsletterDemoTheme(themeId, url.origin)?.runtime ?? null;
}

export async function loader({
  params,
  url,
}: LoaderFunctionArgs): Promise<QuizRouteData | Response> {
  const slug = params.slug?.toLowerCase();
  if (!slug) {
    return { status: 'not_found' as const, quiz: null, questions: [], sponsorsStyle: null };
  }

  return profileSsgLoader(`/quiz/${slug}`, async () => {
    if (process.env.SKIP_REMOTE_BUILD_ASSET_REFRESH === 'true') {
      return {
        status: 'load_error' as const,
        quiz: null,
        questions: [],
        sponsorsStyle: null,
      };
    }

    try {
      const supabaseClient = createSupabaseClientForLoader(`/quiz/${slug}`);
      return await loadQuizRoute(slug, supabaseClient, url);
    } catch (error) {
      console.warn('⚠️  [quiz loader] Failed to load quiz data:', error);
      return { status: 'load_error' as const, quiz: null, questions: [], sponsorsStyle: null };
    }
  });
}

export async function clientLoader({
  params,
  request,
}: ClientLoaderFunctionArgs): Promise<QuizRouteData | Response> {
  const slug = params.slug?.toLowerCase();
  if (!slug) {
    return { status: 'not_found' as const, quiz: null, questions: [], sponsorsStyle: null };
  }

  const playbackStartedAt = performance.now();
  // Download the gameplay code while the Playback bootstrap resolves. React.lazy consumes this
  // same cached promise later; mounting and visible readiness remain owned by QuizPage.
  void loadQuizGameplayModule().catch(() => undefined);
  // Prerendered loader data is an SEO snapshot, not the gameplay authority. A Quiz keeps a
  // stable slug while publishing advances its Live Edition, so every new browser visit must
  // resolve one immutable Playback bootstrap before starting a game.
  try {
    const url = new URL(request.url);
    const [result, demoTheme] = await Promise.all([
      loadQuizRoute(slug, supabase, url),
      loadDemoTheme(slug, url),
    ]);
    if (!(result instanceof Response) && result.status === 'not_found') {
      return redirectDocument(routes.notFound);
    }
    const playbackBootstrapReadyAt = performance.now();
    return result instanceof Response
      ? result
      : {
          ...result,
          ...(demoTheme ? { demoTheme } : {}),
          playbackStartedAt,
          playbackBootstrapReadyAt,
        };
  } catch (error) {
    console.warn('⚠️  [quiz clientLoader] Failed to load quiz data:', error);
    return { status: 'load_error' as const, quiz: null, questions: [], sponsorsStyle: null };
  }
}

clientLoader.hydrate = true as const;

export { QuizHydrateFallback as HydrateFallback } from '../../src/components/RouteHydrateFallbacks/GameplayHydrateFallbacks';

export const meta: MetaFunction<typeof loader> = ({ loaderData, params }) => {
  if (loaderData?.status === 'not_found') return getNotFoundPageMetadata();
  const title = loaderData?.quiz?.title;
  const metaTitle = title ? `Quiz: ${title} | Atrivial` : 'Quiz | Atrivial';
  const description = title
    ? `Play the ${title} quiz on Atrivial.`
    : 'Play a trivia quiz on Atrivial.';

  const slug = params.slug?.toLowerCase().replace(/\/+$/, '') || '';
  const canonicalUrl = getCanonicalUrl(`/quiz/${slug}`);

  return [
    { title: metaTitle },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
  ];
};

export default function Quiz() {
  const data = useLoaderData<typeof loader>();

  if (data.status === 'not_found') {
    return <QuizUnavailablePage kind="not_found" />;
  }

  if (data.status === 'not_live') {
    return (
      <QuizUnavailablePage
        kind="not_live"
        title={data.quiz?.title ?? 'Quiz'}
        scheduledAt={data.quiz?.scheduled_for}
      />
    );
  }

  if (data.status === 'load_error') {
    return <QuizUnavailablePage kind="load_error" title={data.quiz?.title ?? 'Quiz'} />;
  }

  return (
    <QuizPage
      key={data.quiz.edition_id ?? data.quiz.id}
      initialQuiz={data.quiz}
      initialQuestions={data.questions}
      initialSponsorsStyle={data.sponsorsStyle}
      demoTheme={data.demoTheme}
      playbackStartedAt={data.playbackStartedAt}
      playbackBootstrapReadyAt={data.playbackBootstrapReadyAt}
    />
  );
}
